import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TopbarSearch } from '@/components/ui/TopbarSearch';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { CardList } from '@/components/ui/CardList';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAccounts } from './hooks/useAccounts';
import { cn } from '@/utils/cn';
import { formatBillionCurrency } from '@/utils/format';
import { Building2, Plus, Wallet, ShieldCheck, MoreVertical, CreditCard, Banknote } from 'lucide-react';

export function AccountsListPage() {
  const navigate = useNavigate();
  const { accounts, filters, isLoading, setFilters, deleteAccount } = useAccounts();
  const [deleteId, setDeleteId] = React.useState<string | null>(null);

  const statusMap = {
    active: { label: 'Hoạt động', color: 'bg-emerald-500', text: 'text-emerald-600' },
    inactive: { label: 'Tạm dừng', color: 'bg-amber-500', text: 'text-amber-600' },
    archived: { label: 'Lưu trữ', color: 'bg-slate-400', text: 'text-slate-500' },
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <h1 className="text-lg font-bold text-slate-900">Tài khoản thanh toán</h1>
            <p className="text-sm text-slate-500">Quản lý các tài khoản ngân hàng và ví điện tử của công ty.</p>
          </div>
          <button
            onClick={() => navigate('?tab=account-new')}
            className="flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-700 px-4 text-sm font-bold text-white transition-all hover:bg-emerald-800 active:scale-95 shadow-md shadow-emerald-700/10"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm tài khoản</span>
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1">
            <TopbarSearch
              placeholder="Tìm theo tên, số tài khoản hoặc ngân hàng..."
              value={filters.query}
              onChange={(query) => setFilters({ ...filters, query })}
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
            {(['all', 'active', 'inactive', 'archived'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilters({ ...filters, status })}
                className={cn(
                  'whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-bold transition-all',
                  filters.status === status
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                    : 'bg-slate-50 text-slate-500 border border-transparent hover:bg-slate-100'
                )}
              >
                {status === 'all' ? 'Tất cả' : statusMap[status].label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden rounded-md border border-slate-100 bg-white shadow-sm overflow-hidden md:block">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-50 bg-slate-50/50 text-xs font-bold text-slate-500">
              <th className="p-3 pl-6 font-normal">Tài khoản / Ngân hàng</th>
              <th className="p-3 font-normal">Chủ tài khoản</th>
              <th className="p-3 text-right font-normal">Số dư hiện tại</th>
              <th className="p-3 text-center font-normal">Trạng thái</th>
              <th className="p-3 pr-6 text-right font-normal">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td colSpan={5} className="p-4"><div className="h-10 rounded-md bg-slate-50" /></td>
                </tr>
              ))
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-10 text-center text-slate-400">Không tìm thấy tài khoản nào</td>
              </tr>
            ) : (
              accounts.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3 pl-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                        {item.isDefault ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <CreditCard className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-slate-800 truncate">{item.accountName}</p>
                        <p className="text-[10px] font-bold text-slate-400">
                          {item.bankName} • {item.accountNumber}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="text-xs font-medium text-slate-600">{item.accountHolder}</span>
                  </td>
                  <td className="p-3 text-right">
                    <span className="font-bold text-slate-900">{formatBillionCurrency(item.currentBalance)}</span>
                    <span className="ml-1 text-[10px] font-bold text-slate-400 uppercase">{item.currency}</span>
                  </td>
                  <td className="p-3 text-center">
                    <span className={cn('inline-flex items-center gap-1.5 font-bold text-[10px]', statusMap[item.status].text)}>
                      <div className={cn('h-1.5 w-1.5 rounded-full', statusMap[item.status].color)} />
                      {statusMap[item.status].label}
                    </span>
                  </td>
                  <td className="p-3 pr-6 text-right">
                    <ActionMenu
                      options={[
                        { label: 'Chi tiết', icon: 'ri-eye-line', onClick: () => navigate(`?tab=account-detail&id=${item.id}`) },
                        { label: 'Chỉnh sửa', icon: 'ri-edit-line', onClick: () => navigate(`?tab=account-edit&id=${item.id}`) },
                        { label: 'Đặt mặc định', icon: 'ri-check-double-line', onClick: () => {} },
                        { label: 'Xóa', icon: 'ri-delete-bin-line', variant: 'danger', onClick: () => setDeleteId(item.id) },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card List */}
      <div className="md:hidden">
        <CardList
          items={accounts}
          isLoading={isLoading}
          emptyMessage="Không tìm thấy tài khoản nào"
          renderCard={(item) => (
            <div key={item.id} className="rounded-md border border-slate-100 bg-white p-3 shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                    <CreditCard className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-800 truncate">{item.accountName}</p>
                    <p className="text-[10px] font-bold text-slate-400">{item.bankName} • {item.accountNumber}</p>
                  </div>
                </div>
                <ActionMenu
                  options={[
                    { label: 'Chi tiết', icon: 'ri-eye-line', onClick: () => navigate(`?tab=account-detail&id=${item.id}`) },
                    { label: 'Chỉnh sửa', icon: 'ri-edit-line', onClick: () => navigate(`?tab=account-edit&id=${item.id}`) },
                    { label: 'Xóa', icon: 'ri-delete-bin-line', variant: 'danger', onClick: () => setDeleteId(item.id) },
                  ]}
                />
              </div>
              <div className="flex items-center justify-between text-[11px] font-bold pt-2 border-t border-slate-50">
                <div className="flex flex-col">
                  <span className="text-slate-400 uppercase tracking-tighter">Số dư</span>
                  <span className="text-slate-900 text-sm">{formatBillionCurrency(item.currentBalance)} {item.currency}</span>
                </div>
                <span className={cn('inline-flex items-center gap-1.5', statusMap[item.status].text)}>
                  <div className={cn('h-1.5 w-1.5 rounded-full', statusMap[item.status].color)} />
                  {statusMap[item.status].label}
                </span>
              </div>
            </div>
          )}
        />
      </div>

      <ConfirmDialog
        isOpen={!!deleteId}
        title="Xác nhận xóa"
        description="Bạn có chắc chắn muốn xóa tài khoản này? Hành động này không thể hoàn tác."
        confirmLabel="Xóa ngay"
        cancelLabel="Hủy"
        variant="danger"
        onConfirm={() => {
          if (deleteId) deleteAccount(deleteId);
          setDeleteId(null);
        }}
        onClose={() => setDeleteId(null)}
      />
    </div>
  );
}
